import * as React from 'react';
import { Autocomplete, Badge, Box, Button, Menu, MenuItem, TableRowProps, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import Device from '../../models/device';
import DeviceCommand from './DeviceCommand';
import EditableField from './editableField';
import ConfigurationInput from './ConfigurationInput';
import FieldInfo from './deviceFieldInfo';
import { UserFetchJson, UserPostJson } from '../../userFetch';
import TableRow from '../../components/table/TableRow';
import { deviceDrawerFields, deviceTableFields } from './devicefieldsConfig';
import Command from '../../models/command';
import DeviceTableCell, { CellHandlers } from './deviceTableCell';
import { useContextSelector } from 'use-context-selector';
import { DevicesContext } from './deviceContext';
import { isValidMacAddress } from '../../utils';
import useOwnerNames from '../../hooks/useOwnerNames';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PhotosDialog from './PhotosDialog';
import { onWsEvent } from '../../websocket';

const getSpecialFieldValue = (device: Device, field: keyof Device): string => {
    if (field === 'model' && device.memory_size) {
        return `${device.model || ''}${(device.memory_size) ? ` (${device.memory_size}GB)` : ''}`;
    }

    let value = String(device[field] || '');

    // Format MAC address to uppercase if it's a mac field
    if (field === 'mac' && value && isValidMacAddress(value)) {
        value = value.toUpperCase();
    }

    return value;
};

interface DeviceRowPropsTypes {
    serialNumber: string;
    userId: string;
    isSuperAdmin?: boolean;
    isSelected: boolean;
    checkboxID: string;
    commandInputRef?: React.Ref<HTMLInputElement>;
    onCheck?: (serial_number: string, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    tableRowProps?: TableRowProps;
}

function DeviceRow(props: DeviceRowPropsTypes) {
    const command = useContextSelector(DevicesContext, (c) => c.state.devices[props.serialNumber].command);
    const devicesDispatch = useContextSelector(DevicesContext, (c) => c.dispatch);
    const updateDevice = useContextSelector(DevicesContext, (c) => c.updateDevice);
    const deleteDevice = useContextSelector(DevicesContext, (c) => c.deleteDevice);
    const device = useContextSelector(DevicesContext, (c) => c.state.devices[props.serialNumber]);
    const isOngoingCommand = useContextSelector(DevicesContext, (c) => !!c.state.ongoingCommand[props.serialNumber]);
    const lastHeartbeatStale = useContextSelector(DevicesContext, (c) => c.state.devices[props.serialNumber].lastHeartbeatStale);
    const { ownerNames } = useOwnerNames();

    const [configuration, setConfiguration] = React.useState('');
    const drawerOpenRef = React.useRef(false);
    const [userPermissions, setUserPermissions] = React.useState(undefined as { view?: boolean, command?: boolean, configuration?: boolean } | undefined);
    const [ongoingCommand, setOngoingCommand] = React.useState<Command | undefined>(command);
    const [commandRefreshInterval, setCommandRefreshInterval] = React.useState<NodeJS.Timeout | undefined>(undefined);
    const [actionMenuAnchor, setActionMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [photosOpen, setPhotosOpen] = React.useState(false);
    const [photoCount, setPhotoCount] = React.useState(0);

    const cellHandlers = React.useMemo<CellHandlers>(() => {
        return {
            isSelected: props.isSelected,
            checkboxID: props.checkboxID,
            heartbeatStale: lastHeartbeatStale,
            subscriptionStatus: device.subscription_status,
            canEditLabel: props.isSuperAdmin,
            showCheckbox: props.isSuperAdmin,
            status: device.status,
            issues: device.issues,
            onCheck: (event) => {
                props.onCheck?.(props.serialNumber, event);
            },
            refresh: () => {
                devicesDispatch({ type: 'RELOAD_DEVICES' });
            },
            updateLabel: (label: string) => {
                return updateDevice({
                    serial_number: props.serialNumber,
                    label: label
                });
            }
        } as CellHandlers;
    }, [props.isSelected, props.checkboxID, props.onCheck, props.isSuperAdmin, devicesDispatch, props.serialNumber, updateDevice, lastHeartbeatStale, device.subscription_status, device.status, device.issues]);

    const handleDrawyerChange = React.useCallback((opened = true) => {
        drawerOpenRef.current = opened;
        if (opened && !userPermissions) {
            UserFetchJson(`/api/devices/${props.serialNumber}/permissions/${props.userId}`)
                .then((response) => {
                    setUserPermissions(response);
                });
            UserFetchJson(`/api/devices/${props.serialNumber}/photos/count`)
                .then((response: { count: number }) => {
                    setPhotoCount(response.count);
                })
                .catch(() => {});
        } else if (opened && userPermissions?.configuration) {
            UserFetchJson(`/api/devices/${props.serialNumber}/configuration`)
                .then((response: { configuration: string }) => {
                    setConfiguration(response.configuration);
                });
        }
    }, [props.serialNumber, props.userId, userPermissions]);

    const handleSaveConfiguration = React.useCallback((newConfiguration: string) => {
        UserPostJson(`/api/devices/${props.serialNumber}/configuration`, {
            configuration: newConfiguration
        })
            .then((response: { configuration: string }) => {
                setConfiguration(response.configuration);
            })
    }, [props.serialNumber]);

    const refreshCommand = React.useCallback(() => {
        UserFetchJson(`/api/devices/${props.serialNumber}/command?ignore_fetch=true`)
            .then((command: { result: string, requested?: string, completed?: string } | undefined) => {
                setOngoingCommand(command);

                if (command?.requested && !command?.completed) {
                    devicesDispatch({
                        type: 'SENDING_COMMAND',
                        payload: [{
                            serial_number: props.serialNumber,
                            command: command.completed
                        }]
                    });
                }
            });
    }, [props.serialNumber]);

    const updateTags = React.useCallback((tags: string) => {
        return updateDevice({
            serial_number: props.serialNumber,
            tags
        });
    }, [props.serialNumber, updateDevice]);

    const updateLocation = React.useCallback((location: string) => {
        return updateDevice({
            serial_number: props.serialNumber,
            location
        });
    }, [props.serialNumber, updateDevice]);

    const updateOwner = React.useCallback((owner: string) => {
        return updateDevice({
            serial_number: props.serialNumber,
            owner
        });
    }, [props.serialNumber, updateDevice]);

    React.useEffect(() => {
        return onWsEvent('command:status', (msg) => {
            if (msg.serialNumber === props.serialNumber && msg.command) {
                setOngoingCommand(msg.command);
            }
        });
    }, [props.serialNumber]);

    React.useEffect(() => {
        if (!!ongoingCommand?.requested && !ongoingCommand?.completed) {
            if (!commandRefreshInterval) {
                // Fallback polling in case WS is disconnected
                setCommandRefreshInterval(setInterval(refreshCommand, 10000));
            }
        } else {
            devicesDispatch({
                type: 'COMMAND_SENT',
                payload: [{
                    serial_number: props.serialNumber
                }]
            });

            clearInterval(commandRefreshInterval);
            setCommandRefreshInterval(undefined);
        }
    }, [ongoingCommand, commandRefreshInterval, ongoingCommand?.requested, ongoingCommand?.completed]);

    React.useEffect(() => {
        refreshCommand();
    }, []);

    React.useEffect(() => {
        if (isOngoingCommand) {
            refreshCommand();
        }
    }, [isOngoingCommand]);

    React.useEffect(() => {
        if (userPermissions && userPermissions.configuration && drawerOpenRef.current) {
            UserFetchJson(`/api/devices/${props.serialNumber}/configuration`)
                .then((response: { configuration: string }) => {
                    setConfiguration(response.configuration);
                });
        }
    }, [userPermissions?.configuration, props.serialNumber, device.last_heartbeat]);

    React.useEffect(() => { // Dismount
        return () => {
            clearInterval(commandRefreshInterval);
        }
    }, [commandRefreshInterval]);

    const sendCommand = React.useCallback((command: string, payload: string) => {
        UserPostJson(`/api/devices/${props.serialNumber}/command`, {
            operation: command,
            payload: payload
        }).then(() => {
            devicesDispatch({
                type: 'SENDING_COMMAND',
                payload: [{
                    serial_number: props.serialNumber,
                    command,
                }]
            });
            refreshCommand();
        });
    }, [props.serialNumber, devicesDispatch, refreshCommand]);

    return (
        <>
        <TableRow
            data={props.serialNumber}
            isSelected={props.isSelected}
            drawerColumnSpan={deviceTableFields.length - 1}
            tableRowProps={{
                ...(props.tableRowProps || {}),
                'aria-checked': props.isSelected,
                'key': props.serialNumber,
            }}
            drawerButtonBar={
                <>
                    <Box>
                        <Button
                            variant="outlined"
                            fullWidth
                            sx={{
                                color: '#f75961',
                                borderColor: '#f75961',
                                '&:hover': {
                                    borderColor: '#f75961',
                                    backgroundColor: 'rgba(247, 89, 97, 0.1)'
                                }
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setActionMenuAnchor(e.currentTarget);
                            }}>
                            <RestartAltIcon />
                        </Button>
                        <Menu
                            anchorEl={actionMenuAnchor}
                            open={Boolean(actionMenuAnchor)}
                            onClose={() => setActionMenuAnchor(null)}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MenuItem onClick={() => {
                                setActionMenuAnchor(null);
                                sendCommand('refresh', '');
                            }}>Refresh Page</MenuItem>
                            <MenuItem onClick={() => {
                                setActionMenuAnchor(null);
                                sendCommand('restartbrowser', '');
                            }}>Restart Browser</MenuItem>
                            <MenuItem onClick={() => {
                                setActionMenuAnchor(null);
                                if (window.confirm('Are you sure you want to reboot this device?')) {
                                    sendCommand('reboot', '');
                                }
                            }}>Reboot Device</MenuItem>
                            <MenuItem onClick={() => {
                                setActionMenuAnchor(null);
                                if (window.confirm('Are you sure you want to shut down this device?')) {
                                    sendCommand('halt', '');
                                }
                            }}>Shut Down</MenuItem>
                        </Menu>
                    </Box>

                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={(e) => {
                            e.stopPropagation();
                            setPhotosOpen(true);
                        }}
                        sx={{
                            position: 'relative',
                            color: '#f75961',
                            borderColor: '#f75961',
                            '&:hover': {
                                borderColor: '#f75961',
                                backgroundColor: 'rgba(247, 89, 97, 0.1)'
                            }
                        }}>
                        <PhotoCameraIcon />
                        {photoCount > 0 && (
                            <Box component="span" sx={{
                                position: 'absolute',
                                right: 6,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                borderRadius: '50%',
                                width: 20,
                                height: 20,
                                fontSize: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                {photoCount}
                            </Box>
                        )}
                    </Button>

                    {props.isSuperAdmin && <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 2 }}>
                        <Button
                            variant="outlined"
                            href={`vnc://${device.ip_private}`}
                            sx={{
                                color: '#f75961',
                                borderColor: '#f75961',
                                '&:hover': {
                                    borderColor: '#f75961',
                                    backgroundColor: 'rgba(247, 89, 97, 0.1)'
                                }
                            }}>
                            VNC
                        </Button>

                        <Button
                            variant="outlined"
                            href={`sshterm://${device.ip_private}`}
                            sx={{
                                color: '#f75961',
                                borderColor: '#f75961',
                                '&:hover': {
                                    borderColor: '#f75961',
                                    backgroundColor: 'rgba(247, 89, 97, 0.1)'
                                }
                            }}>
                            SSH
                        </Button>
                    </Box>}

                    {props.isSuperAdmin && userPermissions?.configuration &&
                        <Button
                            variant="outlined"
                            sx={{
                                mt: 2,
                                color: '#f75961',
                                borderColor: '#f75961',
                                '&:hover': {
                                    borderColor: '#f75961',
                                    backgroundColor: 'rgba(247, 89, 97, 0.1)'
                                }
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Are you sure you want to delete this device? This action cannot be undone.')) {
                                    deleteDevice(props.serialNumber);
                                }
                            }}>
                            <DeleteIcon />
                        </Button>
                    }
                </>}
            drawerChildren={
                <>
                    {userPermissions?.command && <>
                        <DeviceCommand
                            label='Command'
                            direction='column'
                            serial_number={props.serialNumber}
                            ongoingCommand={ongoingCommand}
                            onCommandChange={refreshCommand}
                            commandInputRef={props.commandInputRef} />
                    </>}

                    {userPermissions && userPermissions.configuration && <ConfigurationInput
                        configuration={configuration}
                        onSave={handleSaveConfiguration}
                        readOnly={!props.isSuperAdmin}
                    />}

                    <Grid container spacing={2} sx={{ alignContent: 'start' }}>
                        {deviceDrawerFields.map(({ field, label }) => {
                            if (field === 'owner') {
                                return (
                                    <Grid size={{ xs: 6 }} key={String(field)}>
                                        {props.isSuperAdmin ? (
                                            <Autocomplete
                                                options={ownerNames}
                                                value={device.owner || null}
                                                onChange={(_e, value) => updateOwner(value || '')}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Owner"
                                                        placeholder="add owner"
                                                        fullWidth
                                                        variant="standard"
                                                        slotProps={{ inputLabel: { shrink: true } }}
                                                    />
                                                )}
                                                size="small"
                                            />
                                        ) : (
                                            <FieldInfo
                                                value={device.owner || ''}
                                                label={label}
                                                textfieldProps={{ fullWidth: true, slotProps: { inputLabel: { shrink: true } } }}
                                            />
                                        )}
                                    </Grid>
                                );
                            }
                            return (
                                <Grid size={{ xs: 6 }} key={String(field) || 'checkbox'}>
                                    <FieldInfo
                                        value={getSpecialFieldValue(device, field)}
                                        label={label}
                                        textfieldProps={{ fullWidth: true, slotProps: { inputLabel: { shrink: true } } }}
                                    />
                                </Grid>
                            );
                        })}

                        {props.isSuperAdmin && <Grid size={{ xs: 12 }}>
                            <EditableField
                                label='Tags'
                                placeholder='add tags'
                                value={device.tags || ''}
                                textfieldProps={{ fullWidth: true, slotProps: { inputLabel: { shrink: true } } }}
                                onUpdate={updateTags}
                            />
                        </Grid>}

                        <Grid size={{ xs: 12 }}>
                            <EditableField
                                label='Location'
                                placeholder='add location'
                                value={device.location || ''}
                                textfieldProps={{ fullWidth: true, slotProps: { inputLabel: { shrink: true } } }}
                                onUpdate={updateLocation}
                            />
                        </Grid>
                    </Grid>
                </>}
            drawerOpened={handleDrawyerChange}
        >
            {
                deviceTableFields.map((field) => {
                    return (
                        <DeviceTableCell
                            key={field || 'checkbox'}
                            value={field && device[field] != null ? String(device[field]) : ''}
                            field={field}
                            handlers={cellHandlers}
                            ongoingCommand={ongoingCommand}
                        />
                    );
                })
            }
        </TableRow >

            {photosOpen && (
                <PhotosDialog
                    open={photosOpen}
                    onClose={() => setPhotosOpen(false)}
                    serialNumber={props.serialNumber}
                    deviceLabel={device.label || ''}
                    isSuperAdmin={!!props.isSuperAdmin}
                    onCountChange={setPhotoCount}
                />
            )}
        </>
    );
}

export default DeviceRow;